<template>
  <div
    class="max-w-6xl mx-auto mb-4"
  >
    <p class="text-sm text-gray-500 mt-2 mb-4 max-w-sm md:max-w-full">
      To speed up the onboarding process, you can also select an option below:
    </p>
    <div>
      <div>
        <div class="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <button
            type="button"
            class="relative rounded-lg border bg-white px-5 py-4 shadow flex items-center space-x-3 text-left w-full hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
            :class="{ 'bg-indigo-50 border-indigo-500': selectedOnboardingHint === ONBOARDING_FULL_ASSISTANCE }"
            @click="toggleWidget('toggleWidgetB2BTheme')"
          >
            <div class="flex-shrink-0 text-7xl">
              🌅
            </div>
            <div class="flex-1 min-w-0">
              <a
                ref="request-install"
                class="focus:outline-none"
              >
                <span
                  class="absolute inset-0"
                  aria-hidden="true"
                />
                <p class="text-sm font-medium text-gray-900">Use our B2B theme</p>
                <p class="xl:text-xs text-sm text-gray-500 mb-1">Install our optimised Dawn theme with zero coding</p>
                <span
                  class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
                >
                  <ClockIcon class="group-hover:text-spark-purple mr-1 h-3 w-3" />
                  <span>Instant setup</span>
                </span>
              </a>
            </div>
          </button>
          <button
            type="button"
            class="relative rounded-lg border bg-white px-5 py-4 shadow flex items-center space-x-3 text-left w-full hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
            :class="{ 'bg-indigo-50 border-indigo-500': selectedOnboardingHint === ONBOARDING_ASSIST_IF_NEEDED }"
            @click="toggleWidget('toggleWidgetRequestInstall')"
          >
            <div class="flex-shrink-0 text-6xl">
              🛠️
            </div>
            <div class="flex-1 min-w-0">
              <a
                ref="b2b-theme"
                class="focus:outline-none"
              >
                <span
                  class="absolute inset-0"
                  aria-hidden="true"
                />
                <p class="text-sm font-medium text-gray-900">Request installation</p>
                <p class="xl:text-xs text-sm text-gray-500 mb-1">Let our team install the B2B storefront widgets for you</p>
                <span
                  class="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800"
                >
                  <ClockIcon class="group-hover:text-spark-purple mr-1 h-3 w-3" />
                  <span>Typically 1 day</span>
                </span>
              </a>
            </div>
          </button>
          <button
            type="button"
            class="relative rounded-lg border bg-white px-5 py-4 shadow flex items-center space-x-3 text-left w-full hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
            :class="{ 'bg-indigo-50 border-indigo-500': selectedOnboardingHint === ONBOARDING_NO_ASSISTANCE }"
            @click="toggleWidget('toggleWidgetSelfInstall')"
          >
            <div class="flex-shrink-0 text-5xl">
              😎
            </div>
            <div class="flex-1 min-w-0">
              <a
                ref="self-install"
                class="focus:outline-none"
              >
                <span
                  class="absolute inset-0"
                  aria-hidden="true"
                />
                <p class="text-sm font-medium text-gray-900">Install yourself</p>
                <p class="xl:text-xs text-sm text-gray-500 mb-1">See our guides on how to apply the code changes</p>
                <span
                  class="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800"
                >
                  <ClockIcon class="group-hover:text-spark-purple mr-1 h-3 w-3" />
                  <span>Typically 1-2 hours</span>
                </span>
              </a>
            </div>
          </button>
        </div>
      </div>
      <!-- OPTION 1 -->
      <div
        v-if="selectedOnboardingHint === ONBOARDING_FULL_ASSISTANCE && toggleWidgetB2BTheme"
        class="bg-indigo-50 border border-indigo-500 shadow rounded-lg mt-6"
      >
        <div class="px-5 py-5 sm:p-6 flex">
          <span class="w-12 mr-6 h-12 p-2 hidden md:flex justify-center align-middle items-center text-5xl">
            🌅
          </span>
          <div>
            <h3 class="text-sm font-medium text-gray-600">
              Try SparkLayer with our ready-made B2B Dawn theme!
            </h3>
            <div class="mt-1 sm:pr-8 text-sm text-gray-500">
              <p class="mb-2">
                Our ready-made
                <a
                  href="https://docs.sparklayer.io/shopify-b2b-theme"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                >
                  <span>Shopify B2B Dawn Theme </span>
                  <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                </a>
                comes preloaded with all the SparkLayer widgets, enabling everything you need to get
                started! Our installation process works by automatically installing an 'unpublished'
                theme in your Shopify theme library that you can then use to complete the onboarding
                process and test how SparkLayer works.
              </p>
              <p class="mb-4">
                The demo theme has <span class="font-medium">no impact to your live Shopify store</span> and can be removed at any point. <a
                  href="https://docs.sparklayer.io/shopify-b2b-theme#2ciNA"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                >
                  <span>Learn more</span>
                  <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                </a>
              </p>
              <div class="flex flex-wrap relative">
                <ThemeInstallButton @installed="dismissThemeInstallHint()" />
                <button
                  class="inline-flex mt-2 sm:mt-0 text-center sm:ml-2 intercom-onboarding-1 justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full sm:w-auto"
                  type="button"
                  @click="closeWidget(); dismissThemeInstallHint()"
                >
                  <span>Close</span>
                </button>
              </div>
            </div>
            <div
              class="mt-3 text-xs text-gray-500 block sm:flex justify-between"
            >
              The installation only takes a moment and you'll see a theme called 'SparkLayer Dawn
              Theme' appear in your Shopify admin.<br>Please note, clicking the button will install
              a new theme each time.
            </div>
          </div>
        </div>
      </div>
      <!-- OPTION 2 -->
      <div
        v-if="selectedOnboardingHint === ONBOARDING_ASSIST_IF_NEEDED && toggleWidgetRequestInstall"
        class="bg-indigo-50 border border-indigo-500 shadow rounded-lg mt-6"
      >
        <div class="px-5 py-5 sm:p-6 flex">
          <span class="w-12 mr-6 h-12 p-2 text-5xl hidden md:flex justify-center align-middle items-center ">
            🛠️
          </span>
          <div>
            <h3 class="text-sm font-medium text-gray-600">
              Request installation on your current Shopify theme
            </h3>
            <div class="mt-1 sm:pr-8 text-sm text-gray-500">
              <p class="mb-2">
                Our expert team can help set up the special
                <router-link
                  to="/configuration/frontend/core"
                  class="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  SparkLayer frontend code snippets
                </router-link>
                on your Shopify store and some example B2B pricing. Once we're done, we can then arrange an onboarding call to walk you through it. We're normally able to complete this within 1 working day and there's <span class="font-medium">no charge for this service</span>!
              </p>
              <p class="mb-4">
                Looking to quickly test SparkLayer before installing on your live site? Try our
                <button
                  type="button"
                  class="font-medium text-indigo-600 hover:text-indigo-500 cursor-pointer"
                  @click="selectedOnboardingHint = ONBOARDING_FULL_ASSISTANCE"
                >
                  Shopify B2B Dawn Theme
                </button>
                that lets you test how SparkLayer works with no coding required!
              </p>
              <a
                class="trigger-intercom-install w-full sm:w-auto justify-center cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <span>Request installation</span>
              </a>
              <button
                type="button"
                class="inline-flex mt-2 sm:mt-0 text-center sm:ml-2 intercom-onboarding-1 justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full sm:w-auto"
                @click="closeWidget()"
              >
                <span>Close</span>
              </button>
            </div>
            <div
              class="mt-4 text-xs text-gray-500 block sm:flex justify-between"
            >
              When you request installation, you'll be asked to grant us temporary collaborator
              access to your Shopify store.
            </div>
          </div>
        </div>
      </div>
      <!-- OPTION 3 -->
      <div
        v-if="selectedOnboardingHint === ONBOARDING_NO_ASSISTANCE && toggleWidgetSelfInstall"
        class="bg-indigo-50 border border-indigo-500 shadow rounded-lg mt-6"
      >
        <div class="px-5 py-5 sm:p-6 flex">
          <span class="w-12 mr-6 h-12 p-2 text-5xl hidden md:flex justify-center align-middle items-center ">
            😎
          </span>
          <div>
            <h3 class="text-sm font-medium text-gray-600">
              Install SparkLayer yourself (we're still here to help!)
            </h3>
            <div class="mt-1 sm:pr-8 text-sm text-gray-500">
              <p class="mb-2">
                If you're experienced in working with Shopify and the Shopify liquid templating
                language, you should find our onboarding process a breeze. When you get to adding
                the special
                <router-link
                  to="/configuration/frontend/core"
                  class="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  SparkLayer frontend code snippets
                </router-link>
                on your Shopify store, we recommend watching our <a
                  href="https://www.youtube.com/watch?v=Kt1AuYf1OQQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
                >
                  <span>onboarding video </span>
                  <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
                </a> to see how it's done. Once you've completed that step, you can then set up your
                pricing and customer rules.
              </p>
              <p class="mb-4">
                Looking to quickly test SparkLayer before installing on your live site? Try our
                <button
                  type="button"
                  class="font-medium text-indigo-600 hover:text-indigo-500 cursor-pointer"
                  @click="selectedOnboardingHint = ONBOARDING_FULL_ASSISTANCE"
                >
                  Shopify B2B Dawn Theme
                </button>
                that lets you test how SparkLayer works with no coding required!
              </p>
              <router-link
                to="/configuration/frontend/core"
                class="trigger-intercom-install cursor-pointer w-full sm:w-auto justify-center inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <span>See installation steps</span>
              </router-link>
              <button
                type="button"
                class="inline-flex mt-2 sm:mt-0 text-center sm:ml-2 intercom-onboarding-1 justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full sm:w-auto"
                @click="closeWidget()"
              >
                <span>Close</span>
              </button>
            </div>
            <div
              class="mt-4 text-xs text-gray-500 block sm:flex justify-between"
            >
              If you get stuck, don't worry. We can help with the installation process!
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ExternalLinkIcon, ClockIcon,
} from '@heroicons/vue/outline';
import ThemeInstallButton from '@/components/Platforms/Shopify/ThemeInstallButton.vue';

const THEME_INSTALL_DISMISSED = 'THEME_INSTALL_DISMISSED';

export default {
  name: 'FrontendHints',
  components: {
    ThemeInstallButton,
    ExternalLinkIcon,
    ClockIcon,
  },
  props: {
    hideOnboardingHint: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedOnboardingHint:
        this.hideOnboardingHint || localStorage.getItem(THEME_INSTALL_DISMISSED)
          ? null
          : 'ONBOARDING_FULL_ASSISTANCE',
      ONBOARDING_FULL_ASSISTANCE: 'ONBOARDING_FULL_ASSISTANCE',
      ONBOARDING_ASSIST_IF_NEEDED: 'ONBOARDING_ASSIST_IF_NEEDED',
      ONBOARDING_NO_ASSISTANCE: 'ONBOARDING_NO_ASSISTANCE',
      toggleWidgetRequestInstall: false,
      toggleWidgetB2BTheme: false,
      toggleWidgetSelfInstall: false,
    };
  },
  mounted() {
    if (this.$route.query.expand === 'widgets' && this.$route.query.widget) {
      this.expandSection(
        Array.isArray(this.$route.query.widget)
          ? this.$route.query.widget[0]
          : this.$route.query.widget,
      );
    }
  },
  methods: {
    dismissThemeInstallHint() {
      localStorage.setItem(THEME_INSTALL_DISMISSED, 'true');
    },
    expandSection(section) {
      switch (section) {
        case 'request-install':
          this.selectedOnboardingHint = this.ONBOARDING_ASSIST_IF_NEEDED;
          this.toggleWidgetRequestInstall = true;
          break;
        case 'b2b-theme':
          this.selectedOnboardingHint = this.ONBOARDING_FULL_ASSISTANCE;
          this.toggleWidgetB2BTheme = true;
          break;
        case 'self-install':
          this.selectedOnboardingHint = this.ONBOARDING_NO_ASSISTANCE;
          this.toggleWidgetSelfInstall = true;
          break;
        default:
      }
    },
    toggleWidget(widget) {
      switch (widget) {
        case 'toggleWidgetRequestInstall':
          this.selectedOnboardingHint = this.ONBOARDING_ASSIST_IF_NEEDED;
          this.toggleWidgetRequestInstall = !this.toggleWidgetRequestInstall;
          this.toggleWidgetB2BTheme = false;
          this.toggleWidgetSelfInstall = false;
          break;
        case 'toggleWidgetB2BTheme':
          this.selectedOnboardingHint = this.ONBOARDING_FULL_ASSISTANCE;
          this.toggleWidgetRequestInstall = false;
          this.toggleWidgetB2BTheme = !this.toggleWidgetB2BTheme;
          this.toggleWidgetSelfInstall = false;
          break;
        case 'toggleWidgetSelfInstall':
          this.selectedOnboardingHint = this.ONBOARDING_NO_ASSISTANCE;
          this.toggleWidgetRequestInstall = false;
          this.toggleWidgetB2BTheme = false;
          this.toggleWidgetSelfInstall = !this.toggleWidgetSelfInstall;
          break;
        default:
      }
    },
    closeWidget() {
      this.selectedOnboardingHint = null;
      this.toggleWidgetRequestInstall = false;
      this.toggleWidgetB2BTheme = false;
      this.toggleWidgetSelfInstall = false;
    },
  },
};
</script>
